import { msg, select } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowUpward from '@mui/icons-material/North';
import {
  Badge,
  Box,
  Button,
  Menu,
  MenuItem,
  Tab,
  type TabProps,
  Tabs,
  Tooltip,
  type TooltipProps,
  tabClasses,
} from '@mui/material';
import { forwardRef, type MouseEvent, memo, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useDialogStore } from '@/components/dialogs';
import { OneClickTooltipContent } from '@/components/one_click_tooltip_content';
import { OrderType } from '@/feature/order_entry/order_types';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useOrderFormContext } from '../order_context';
import type { OrderFormData } from '../order_schema';
import { getLeverageIncrease } from './leverageIncreaseNotice';

const stopTab = 'stop';

const TooltipTab = memo(
  forwardRef<
    HTMLDivElement,
    TabProps & {
      tooltipTitle?: ReactNode;
      enterDelay?: number;
      enterNextDelay?: number;
      arrow?: boolean;
      placement?: TooltipProps['placement'];
    }
  >((props, ref) => {
    const { tooltipTitle, enterDelay, enterNextDelay, arrow, placement, ...tabProps } = props;
    return (
      <Tooltip title={tooltipTitle} enterDelay={enterDelay} enterNextDelay={enterNextDelay} arrow={arrow} placement={placement}>
        <Tab ref={ref} {...tabProps} />
      </Tooltip>
    );
  })
);

const tabsSx = {
  flex: 1,
  [`& .${tabClasses.fullWidth}`]: {
    px: 0.5,
    fontSize: '0.75rem',
    lineHeight: '20px',
  },
  [`& .${tabClasses.root}`]: {
    px: { xs: 0.25, md: 0.5 },
    minWidth: 0,
    // Content-sized on narrow forms so "Market" isn't clipped while "Limit" has spare room.
    flex: { xs: '1 1 auto', md: 1 },
    fontSize: '0.75rem',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
  },
};

const tooltipTabSx = {
  [`& .${tabClasses.icon}`]: {
    ml: 0,
    height: { xs: 16, md: 20 },
    width: { xs: 16, md: 20 },
  },
};

const leverageSx = {
  minWidth: 'auto',
  px: { xs: 1, md: 2 },
  py: 0.5,
  ml: { xs: 0.5, md: 2 },
};

// Figma 13447:6518: a 6px dot flush against the label's top-right corner. MUI's dot is 8px and
// centred on that corner, so +3/-3 cancels its translate(50%, -50%) — offsets rather than
// `transform: none`, which would break MUI's scale(0) hide for `invisible`.
const makerBoostBadgeSx = {
  '& .MuiBadge-badge': {
    height: 6,
    minWidth: 6,
    top: 3,
    right: -3,
  },
};

const leverageBadgeSx = {
  '& .MuiBadge-badge': {
    minWidth: 10,
    height: 10,
    borderRadius: '50%',
    // Nudge down/left off the default corner anchor so the dot straddles the corner.
    top: '2px',
    right: '2px',
  },
};

export const OrderTypeBar = memo(function OrderTypeBar() {
  const { i18n, t } = useLingui();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const form = useFormContext<OrderFormData>();
  const { field } = useController({
    name: 'orderType',
    control: form.control,
  });
  const { triggerOrdersEnabled, marketData, makerBoostEnabled } = useOrderFormContext();
  const open = Boolean(anchorEl);
  const isMobile = useIsMobile();

  const { openDialog } = useDialogStore();
  const leverage = useWatch({
    control: form.control,
    name: 'leverage',
  });

  const leverageIncreased = getLeverageIncrease(marketData) != null;

  const handleMenuItemClick = useCallback(
    (_: MouseEvent<HTMLLIElement>, value: OrderType) => {
      setAnchorEl(null);
      form.setValue('orderType', value);
    },
    [form]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (!triggerOrdersEnabled && [OrderType.stopLimit, OrderType.stopMarket].includes(field.value)) {
      form.setValue('orderType', OrderType.limit);
    }
  }, [triggerOrdersEnabled, field.value, form]);

  const stopOrdersActive = triggerOrdersEnabled;
  const stopOrdersTooltip = useMemo(() => (stopOrdersActive ? undefined : <OneClickTooltipContent />), [stopOrdersActive]);

  const currentTabValue = useMemo(
    () => ([OrderType.stopLimit, OrderType.stopMarket].includes(field.value) ? stopTab : field.value),
    [field.value]
  );

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, newValue: any) => {
      // small hack to prevent the tab from changing when the user clicks on the stop tab
      if (newValue === stopTab) return;

      return field.onChange(newValue);
    },
    [field]
  );

  const handleStopTabClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (stopOrdersActive) {
        setAnchorEl(e.currentTarget);
      }
    },
    [stopOrdersActive]
  );

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.25}>
        <Tabs value={currentTabValue} variant="fullWidth" sx={tabsSx} onChange={handleTabChange}>
          <Tab value={OrderType.market} label={t({ comment: 'Market order type', message: 'Market' })} />

          <TooltipTab
            value={OrderType.limit}
            label={
              <Badge color="primary" variant="dot" invisible={!makerBoostEnabled} sx={makerBoostBadgeSx}>
                {t({ comment: 'Limit order type', message: 'Limit' })}
              </Badge>
            }
            tooltipTitle={
              makerBoostEnabled
                ? t({
                    comment: 'Limit order tab tooltip: maker-volume mPoints boost campaign',
                    message: 'Bonus mPoints for adding liquidity',
                  })
                : undefined
            }
            arrow
            placement="bottom"
          />
          <TooltipTab
            value={stopTab}
            label={i18n._(
              msg({
                comment: 'Stop order type',
                message: select({ orderType: field.value }, { stopLimit: 'Stop', stopMarket: 'Stop', other: 'Stop' }),
              })
            )}
            iconPosition="end"
            icon={<ExpandMore />}
            sx={tooltipTabSx}
            onClick={handleStopTabClick}
            tooltipTitle={stopOrdersTooltip}
            enterDelay={0}
            enterNextDelay={0}
          />
        </Tabs>
        <Tooltip
          title={t({
            comment: 'Leverage button tooltip: margin mode restriction',
            message: 'All positions are currently Isolated only.',
          })}
          arrow
          placement="top"
          enterDelay={600}
        >
          <Badge color="primary" variant="dot" invisible={!leverageIncreased} sx={leverageBadgeSx}>
            <Button
              size={isMobile ? 'small' : 'large'}
              variant="outlined"
              onClick={() => openDialog('adjustLeverage')}
              sx={leverageSx}
            >
              {leverage}x{leverageIncreased && <ArrowUpward fontSize="xSmall" sx={{ ml: 0.25 }} />}
            </Button>
          </Badge>
        </Tooltip>
      </Box>

      <Menu
        variant="menu"
        slotProps={{
          paper: {
            variant: 'elevation',
          },
        }}
        anchorEl={anchorEl}
        open={open}
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox',
        }}
        onClose={handleClose}
      >
        <MenuItem
          dense
          selected={field.value === OrderType.stopLimit}
          onClick={(e) => handleMenuItemClick(e, OrderType.stopLimit)}
          disabled={!stopOrdersActive}
        >
          {t({ comment: 'Stop limit order type', message: 'Stop Limit' })}
        </MenuItem>
        <MenuItem
          dense
          selected={field.value === OrderType.stopMarket}
          onClick={(e) => handleMenuItemClick(e, OrderType.stopMarket)}
          disabled={!stopOrdersActive}
        >
          {t({ comment: 'Stop market order type', message: 'Stop Market' })}
        </MenuItem>
      </Menu>
    </>
  );
});
